/**
 * VoiceManager — the pool of Voices plus the logic that decides who plays
 * which note.
 *
 * Three playback modes:
 *   - poly   — N independent voices. Oldest-first stealing when the pool's full.
 *   - mono   — one voice. Every note-on retriggers the envelope.
 *   - legato — one voice. A new note while another is held does NOT retrigger
 *              the envelope (slur). The first note in a phrase does.
 *
 * Unison: in POLY mode, each note allocates `unison` voices (1..7). Their
 * osc.detune offsets spread symmetrically across ±unisonSpread cents. With
 * unison=3 and spread=20, a note produces voices at -20ct / 0ct / +20ct
 * relative to the center pitch — the classic "supersaw" / chorus texture
 * the detune knob has always wanted to participate in.
 */

import type { AudioEngine } from './AudioEngine';
import { Voice, type VoiceTemplate } from './Voice';

export type VoiceMode = 'poly' | 'mono' | 'legato';

export type VoiceLifecycleEvent = 'created' | 'disposed';
export type VoiceLifecycleListener = (event: VoiceLifecycleEvent, voice: Voice) => void;

export interface VoiceManagerOptions {
  readonly polyphony?: number;
  readonly mode?: VoiceMode;
  readonly glideMs?: number;
  readonly unison?: number;
  readonly unisonSpread?: number;
}

const MAX_VOICES = 24;
const MIN_UNISON = 1;
const MAX_UNISON = 7;

export class VoiceManager {
  private voices: Voice[] = [];
  private _polyphony: number;
  private _mode: VoiceMode;
  private _glideMs: number;
  private _unison: number;
  private _unisonSpread: number;

  /** Ordered list of held notes (FIFO) — used by mono/legato for note priority. */
  private readonly heldOrder: number[] = [];
  /**
   * Note → stack of voices playing that note. In poly with unison=1 the
   * stack has one entry; with unison=N it has N; in mono/legato it's [voice].
   */
  private readonly noteToVoices = new Map<number, Voice[]>();
  /** Listeners that track voice allocation (ModRouter uses this to rewire). */
  private readonly lifecycleListeners = new Set<VoiceLifecycleListener>();

  constructor(
    private readonly engine: AudioEngine,
    private readonly template: VoiceTemplate,
    opts: VoiceManagerOptions = {},
  ) {
    this._polyphony = Math.max(1, Math.min(16, opts.polyphony ?? 8));
    this._mode = opts.mode ?? 'poly';
    this._glideMs = Math.max(0, opts.glideMs ?? 0);
    this._unison = clampUnison(opts.unison ?? 1);
    this._unisonSpread = Math.max(0, Math.min(100, opts.unisonSpread ?? 15));
    this.ensureVoiceCount();
  }

  get polyphony(): number {
    return this._polyphony;
  }
  get mode(): VoiceMode {
    return this._mode;
  }
  get glideMs(): number {
    return this._glideMs;
  }
  get unison(): number {
    return this._unison;
  }
  get unisonSpread(): number {
    return this._unisonSpread;
  }
  get voiceList(): readonly Voice[] {
    return this.voices;
  }

  setPolyphony(n: number): void {
    this._polyphony = Math.max(1, Math.min(16, Math.floor(n)));
    if (this._mode === 'mono' || this._mode === 'legato') return;
    this.ensureVoiceCount();
  }

  setMode(mode: VoiceMode): void {
    if (mode === this._mode) return;
    // Hard-cut currently playing notes — a soft release conflicts with the
    // voice-pool reshape that follows, and mode changes are intentional
    // user-driven events where a crisp break is preferable to a release tail.
    this.panic();
    this._mode = mode;
    this.ensureVoiceCount();
  }

  setGlideMs(ms: number): void {
    this._glideMs = Math.max(0, ms);
  }

  /** Voices per note in POLY mode. Clamped to 1..7. */
  setUnison(n: number): void {
    const clamped = clampUnison(n);
    if (clamped === this._unison) return;
    this._unison = clamped;
    this.ensureVoiceCount();
  }

  /** Detune spread (cents) between unison voices. Clamped to 0..100. */
  setUnisonSpread(cents: number): void {
    this._unisonSpread = Math.max(0, Math.min(100, cents));
  }

  /**
   * Start a note. Velocity defaults to full. `when` is an optional
   * AudioContext time; omit for "now".
   */
  noteOn(note: number, velocity = 1, when?: number): void {
    const glideSec = this._glideMs / 1000;

    if (this._mode === 'mono' || this._mode === 'legato') {
      // Mono/legato uses a dedicated pool of exactly `unison` voices. New
      // notes retrigger the whole stack (mono) or the first time in a phrase
      // (legato). Every voice in the stack inherits the per-voice detune
      // offset so unison still thickens the single-note sound.
      const count = Math.min(this._unison, this.voices.length);
      if (count === 0) return;
      const alreadyHeld = this.heldOrder.length > 0;
      const retrigger = this._mode === 'mono' || !alreadyHeld;
      this.pushHeld(note);
      const stack: Voice[] = [];
      for (let i = 0; i < count; i++) {
        const voice = this.voices[i]!;
        voice.setVoiceDetuneOffset(this.computeDetuneOffset(i, count));
        voice.noteOn(note, velocity, when, glideSec, retrigger);
        stack.push(voice);
      }
      this.noteToVoices.set(note, stack);
      return;
    }

    // POLY — allocate `unison` voices, each with a symmetric detune offset.
    const count = this._unison;
    const allocated: Voice[] = [];
    for (let i = 0; i < count; i++) {
      const voice = this.pickVoice(allocated);
      if (!voice) break;
      voice.setVoiceDetuneOffset(this.computeDetuneOffset(i, count));
      voice.noteOn(note, velocity, when, glideSec, true);
      allocated.push(voice);
    }
    if (allocated.length === 0) return;
    this.noteToVoices.set(note, allocated);
  }

  /** Release a note. Silent no-op if that note isn't held. */
  noteOff(note: number, when?: number): void {
    const voices = this.noteToVoices.get(note);
    if (!voices || voices.length === 0) return;
    this.noteToVoices.delete(note);
    this.removeHeld(note);

    if (this._mode === 'mono' || this._mode === 'legato') {
      const fallback = this.heldOrder[this.heldOrder.length - 1];
      if (fallback !== undefined) {
        // Re-assign the whole unison stack to the next-most-recent held note.
        this.noteToVoices.set(fallback, voices);
        const retrigger = this._mode === 'mono';
        const glideSec = this._glideMs / 1000;
        for (const v of voices) {
          v.noteOn(fallback, v.velocity, when, glideSec, retrigger);
        }
        return;
      }
      for (const v of voices) v.noteOff(when);
      return;
    }

    for (const v of voices) v.noteOff(when);
  }

  /** Release every held note. */
  allNotesOff(when?: number): void {
    for (const v of this.voices) v.noteOff(when);
    this.noteToVoices.clear();
    this.heldOrder.length = 0;
  }

  /** Hard cut — zero every voice immediately. Used for panic / mode switches. */
  panic(): void {
    for (const v of this.voices) v.silence();
    this.noteToVoices.clear();
    this.heldOrder.length = 0;
  }

  dispose(): void {
    for (const v of this.voices) {
      this.emitLifecycle('disposed', v);
      v.dispose();
    }
    this.voices.length = 0;
    this.noteToVoices.clear();
    this.heldOrder.length = 0;
    this.lifecycleListeners.clear();
  }

  /**
   * Subscribe to voice create/dispose events. Returns unsubscribe. The
   * ModRouter uses this to keep per-voice AudioParam targets wired correctly
   * across polyphony changes and mode switches.
   */
  subscribeVoices(listener: VoiceLifecycleListener): () => void {
    this.lifecycleListeners.add(listener);
    for (const v of this.voices) listener('created', v);
    return () => {
      this.lifecycleListeners.delete(listener);
    };
  }

  /**
   * Pick a voice for a new note. Allocation order:
   *   1. A genuinely free voice — never causes audible disruption.
   *   2. A voice already in its release phase — fading anyway, the
   *      user won't notice it being cut. Among releasing voices,
   *      pick the one whose release started earliest (most-faded).
   *   3. The oldest sustaining voice — last resort; cuts a held
   *      note but at least it's the one the user is least likely to
   *      still be paying attention to.
   *
   * `exclude` holds voices we've already allocated for the current
   * unison stack so the same chord doesn't keep stealing from itself.
   */
  private pickVoice(exclude: readonly Voice[]): Voice | undefined {
    const free = this.voices.find((v) => !v.active && !exclude.includes(v));
    if (free) return free;

    const candidates = this.voices.filter((v) => !exclude.includes(v));
    if (candidates.length === 0) return undefined;

    // Tier 1: releasing voices. Prefer the one that started earliest
    // (its envelope tail is closest to silent).
    const releasing = candidates.filter((v) => v.isReleasing);
    if (releasing.length > 0) {
      const target = releasing.reduce((a, b) =>
        a.startTime <= b.startTime ? a : b,
      );
      this.evictFromOwner(target);
      return target;
    }

    // Tier 2: oldest sustaining voice.
    const oldest = candidates.reduce((a, b) =>
      a.startTime <= b.startTime ? a : b,
    );
    this.evictFromOwner(oldest);
    return oldest;
  }

  /** Detach a stolen voice from whatever note it was assigned to so a
   *  future noteOff for that note doesn't double-count it. */
  private evictFromOwner(v: Voice): void {
    if (v.note < 0) return;
    const prev = this.noteToVoices.get(v.note);
    if (!prev) return;
    const idx = prev.indexOf(v);
    if (idx >= 0) prev.splice(idx, 1);
    if (prev.length === 0) this.noteToVoices.delete(v.note);
  }

  private computeDetuneOffset(index: number, count: number): number {
    if (count <= 1) return 0;
    // pos: -0.5 .. +0.5 across the stack. Extremes land at ±spread cents.
    const pos = index / (count - 1) - 0.5;
    return pos * 2 * this._unisonSpread;
  }

  private ensureVoiceCount(): void {
    // POLY stacks across multiple simultaneous notes (polyphony × unison).
    // Mono/legato only ever has one note's worth of voices — the unison
    // stack — so its target is just the unison count.
    let target = this._unison;
    if (this._mode === 'poly') {
      target = Math.max(1, Math.min(MAX_VOICES, this._polyphony * this._unison));
    }
    target = Math.max(1, Math.min(MAX_VOICES, target));
    while (this.voices.length < target) {
      const v = new Voice(this.engine, this.template);
      this.voices.push(v);
      this.emitLifecycle('created', v);
    }
    while (this.voices.length > target) {
      const v = this.voices.pop();
      if (v) {
        this.emitLifecycle('disposed', v);
        v.dispose();
      }
    }
  }

  private emitLifecycle(event: VoiceLifecycleEvent, voice: Voice): void {
    for (const fn of this.lifecycleListeners) fn(event, voice);
  }

  private pushHeld(note: number): void {
    this.removeHeld(note);
    this.heldOrder.push(note);
  }

  private removeHeld(note: number): void {
    const idx = this.heldOrder.lastIndexOf(note);
    if (idx >= 0) this.heldOrder.splice(idx, 1);
  }
}

function clampUnison(n: number): number {
  return Math.max(MIN_UNISON, Math.min(MAX_UNISON, Math.floor(n)));
}
